import { Css } from "@homebound/beam";
import { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import { SavePlanBasedBidContractLineItemInput } from "src/generated/graphqlTypes";
import { useRouteParams } from "src/hooks/useRouteParams";
import { createBidPackagesDetailUrl } from "src/RouteUrls";
import { fail, pushInNextLoop, queryResult } from "src/utils";
import { StringParam, useQueryParams } from "use-query-params";
import { PlanBasedBidsTableV2 } from "./components/PlanBasedBidsTableV2";
import {
  EnterPlanBasedBidPage_BidPackageFragment,
  useEnterPlanBasedBidPage_SaveBidContractMutation,
  useEnterPlanBasedBidPageQuery,
} from "./EnterPlanBasedBidPage.generated";
import { getSortedPofs } from "./utils";

export function EnterPlanBasedBidPage() {
  const { bidPackageId, bidderId } = useRouteParams<{ bidPackageId: string; bidderId: string }>();
  const query = useEnterPlanBasedBidPageQuery({ variables: { bidPackageId } });
  return queryResult(query, ({ bidPackage }) => (
    <EnterPlanBasedBidPageView bidPackage={bidPackage} bidderId={bidderId} />
  ));
}

type EnterPlanBasedBidPageViewProps = { bidPackage: EnterPlanBasedBidPage_BidPackageFragment; bidderId: string };

function EnterPlanBasedBidPageView(props: EnterPlanBasedBidPageViewProps) {
  const { bidPackage, bidderId } = props;
  const navigate = useNavigate();
  const [{ productOfferingId }] = useQueryParams({ productOfferingId: StringParam });
  const bidder =
    bidPackage.activeBidders.find((b) => b.id === bidderId) ??
    fail(`Bidder ${bidderId} not found on bid package ${bidPackage.id}`);
  // AddTradePartnerModal always creates a BC + draft BCR for v2 bidders, so the BCR
  // must exist by the time the user lands on this page.
  const bidContractId = bidder.bidContract?.id ?? fail(`Bidder ${bidder.id} has no bid contract`);
  const bidContractRevision =
    bidPackage.latestVersion.latestBidContractRevisions.find((b) => b.bidContract.id === bidContractId) ??
    fail(`Bid contract ${bidContractId} has no revision on the latest version of bid package ${bidPackage.id}`);

  const [saveBidContract] = useEnterPlanBasedBidPage_SaveBidContractMutation();

  const sortedPofs = getSortedPofs(bidPackage);
  const [selectedPofId, setSelectedPofId] = useState(() =>
    productOfferingId && sortedPofs.some((pof) => pof.id === productOfferingId) ? productOfferingId : sortedPofs[0]?.id,
  );

  function detailUrl() {
    return createBidPackagesDetailUrl(bidPackage.bidPackageGroup!.id, bidPackage.id, {
      productOfferingId: selectedPofId,
    });
  }

  const onSave = useCallback(
    async (planBasedLineItems: SavePlanBasedBidContractLineItemInput[]) => {
      // Attach line items to the existing BC; the resolver writes them to the latest revision
      // (cloning into a fresh draft if the latest revision is already signed).
      await saveBidContract({ variables: { input: { id: bidContractId, planBasedLineItems } } });
      pushInNextLoop(navigate, detailUrl());
    },
    [bidContractId, bidPackage, selectedPofId, saveBidContract, navigate],
  );

  function onCancel() {
    pushInNextLoop(navigate, detailUrl());
  }

  return (
    <>
      <div css={Css.xl2.tac.pt4.pb2.fs0.$}>Enter Costs</div>
      <PlanBasedBidsTableV2
        bidPackage={bidPackage}
        mode="edit"
        bidContractRevision={bidContractRevision}
        initiallySelectedTab={selectedPofId}
        onSelectedTabChange={setSelectedPofId}
        onSave={onSave}
        onCancel={onCancel}
      />
    </>
  );
}
